import formatBytes from "core/format/byteFormat";
import {useLocale, useMessages} from "i18n";
import {hostedUploadBytes} from "pricing/planEntitlements";
import type {PaidPlanId} from "pricing/planCatalogue";
import type {UpgradeMomentModel} from "upsell/upgradeMoments";
import {SlideOutCard} from "./SlideOutCard";
import type {SlideOutCardControls} from "./useSlideOutCards";

export interface UpgradeMomentCardProps {
	/** From `upsell/upgradeMoments`; the held moment renders nothing. */
	model: UpgradeMomentModel;
	/** Open or tucked, and the lift: the upsell slot's, owned by the panel through `useSlideOutCards`. */
	controls: SlideOutCardControls;
	/** A plan's monthly price, formatted in the storefront the dialog this card opens will charge in. */
	monthlyPriceOf: (plan: PaidPlanId) => string;
	onAction: () => void;
	onNotNow: () => void;
}

/**
 * The upgrade moments' card (kitecorp/transferify-ui#2909): the send screen's upsell card, armed by what
 * the sender has been doing rather than by a control they touched, in the same slot to the left of the
 * drop area (owner placement, 2026-09-26). The wording is the approved draft; every size and price is
 * the catalogue's. "Not now" is as large as the action and takes the card away for the visit.
 */
export function UpgradeMomentCard({model, controls, monthlyPriceOf, onAction, onNotNow}: UpgradeMomentCardProps) {
	const {sender, pricing, common} = useMessages();
	const {locale} = useLocale();
	const copy = sender.upgradeMoments;
	if (model.moment === "sendingDays") {
		return null;
	}
	const plan = pricing.planNames[model.target];
	const price = monthlyPriceOf(model.target);
	const size = (bytes: number) => formatBytes(bytes, locale, 1);
	const text = (() => {
		switch (model.moment) {
			case "bigFiles":
				return {title: copy.bigFilesTitle, line: copy.bigFilesLine(plan, size(hostedUploadBytes(model.target)), price),
					action: sender.limitCard.upgradeTo(plan)};
			case "repeatRecipient":
				return {title: copy.repeatTitle(model.email),
					line: copy.repeatLine(plan, price),
					action: copy.seePlan(plan)};
			case "unpaidLimit":
				return {title: copy.unpaidTitle,
					line: copy.unpaidLine(size(hostedUploadBytes(model.target)), plan, price),
					action: sender.limitCard.upgradeTo(plan)};
		}
	})();
	return (
		<SlideOutCard
			accent="upsell"
			open={controls.open}
			lifting={controls.lifting}
			offsetTop={controls.offsetTop}
			stacked={controls.stacked}
			onHeight={controls.onHeight}
			onOpen={controls.openCard}
			onTuck={controls.closeCard}
			testId="upgrade-moment-card"
			data-upgrade-moment={model.moment}
			title={text.title}
			line={text.line}
			showLabel={sender.upsellCard.show}
			tuckLabel={common.tuckTag}
			glyph="↑"
			actionLabel={text.action}
			onAction={onAction}
			secondaryLabel={copy.notNow}
			onSecondary={onNotNow}
		/>
	);
}
